// externals
import { RSAA } from 'redux-api-middleware';
import { actionCreators, isDifferentLender } from '@makemydeal/dr-offer-redux';
import type { LenderChangeOptions, LenderIdentity } from '@makemydeal/dr-offer-redux';
import type { AnyAction, Dispatch } from 'redux';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';

// libraries
import { BALLOON, LEASE, type Lender, type ICreditDecisionLender } from '@makemydeal/dr-platform-types';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { DealTabs } from '@makemydeal/dr-shared-types';
import { createValidationEngine } from '@makemydeal/dr-shared-ui-utils';
import {
    lenderValidationConfig,
    LENDER_ADDRESS_FIELD_PATHS
} from '@makemydeal/dr-shared-ui-utils/validationConfigs/lenderValidationConfig';
import { setErrorFields } from './dealScreenErrorActionCreators';
import * as lenderAddressSelectors from '../selectors/lenderAddressSelectors';
import {
    getCurrentOfferType,
    getCreditDecisionLender,
    getTitlingLenderWithAddress,
    getDealXgId,
    getIsProgramQuotes,
    getTraceId
} from '../selectors/offerRedux';
import { getApplicantType, getCommonOrgId } from '../selectors/offerSelectors';

// consts/enums
import {
    DEACTIVATE_LENDERS,
    UPDATE_CREDIT_DECISION_LENDER_WITH_ADDRESS,
    UPDATE_TITLING_LENDER_WITH_ADDRESS,
    FETCH_LENDER_LIST_REQUEST,
    FETCH_LENDER_LIST_SUCCESS,
    FETCH_LENDER_LIST_FAILURE
} from '../actionTypes/lenderActionTypes';
import {
    FETCH_LENDER_ADDRESS_FAILURE,
    FETCH_LENDER_ADDRESS_REQUEST,
    FETCH_LENDER_ADDRESS_SUCCESS,
    CLEAR_LENDER_ADDRESS_ERROR,
    CACHE_LENDER_ADDRESS_EDIT,
    CACHE_LENDER_ITEM
} from '../actionTypes/lenderAddressActionTypes';

// utils
import { apiConfigHelper, locHrefUtil, MANAGER_VIEW_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';
import { DEAL_XG_DEAL_ID } from '@makemydeal/dr-shared-bff-types';
import { deriveCreditDecisionLenderAddress, deriveTitlingLender, toLenderData } from '../utils/lenderAddressUtils';

// types
import type { LenderAddressApiRequest, LenderAddressResponse, StateTree } from '@makemydeal/dr-dash-types';
import type { CreditDecision, SelectedLenderWithAddress } from '@makemydeal/dr-dash-types';
import type { TitlingLender } from '@makemydeal/dr-platform-types';

export const deactivateLenders = (lenders: CreditDecision[] | Lender[]) => ({
    type: DEACTIVATE_LENDERS,
    payload: lenders.map((lender: CreditDecision | Lender) => {
        return { ...lender, isActive: false };
    })
});

export const updateCreditDecisionLenderWithAddress = (lender: SelectedLenderWithAddress, options?: LenderChangeOptions) => ({
    type: UPDATE_CREDIT_DECISION_LENDER_WITH_ADDRESS,
    payload: {
        lender
    },
    ...actionCreators.buildLenderChangeMeta(options)
});

type RepriceAfterLenderChangeArgs = {
    previousLender?: LenderIdentity;
    newLender: LenderIdentity;
    traceIdBefore: string;
};

export const repriceAfterContractingLenderChange =
    ({ previousLender, newLender, traceIdBefore }: RepriceAfterLenderChangeArgs) =>
    (dispatch: Dispatch<AnyAction, StateTree>, getState: () => StateTree) => {
        const state = getState();
        if (!featureToggleSelectors.isReserveSplitOverrideEnabled(state)) return;
        if (getIsProgramQuotes(state)) return;
        const isFirstLender = !previousLender?.id && !previousLender?.name;
        if (!isFirstLender && !isDifferentLender(previousLender, newLender)) return;
        if (getTraceId(state) !== traceIdBefore) return;

        dispatch(actionCreators.repriceForContractingLenderChange());
    };

export const updateTitlingLenderWithAddress = (titlingLender: TitlingLender) => ({
    type: UPDATE_TITLING_LENDER_WITH_ADDRESS,
    payload: {
        titlingLender
    }
});

export const fetchLenderList = (state: StateTree) => {
    const locHref = locHrefUtil.getLocHref();
    const leadRoutingOverrideEnabled = dealerSelectors.getLeadRoutingOverrideSettingsEnabled(state);
    const dealerId = leadRoutingOverrideEnabled
        ? `${dealerSelectors.getLeadOverrideDealerId(state)}`
        : `${dealerSelectors.getDealerId(state)}`;

    const bffApiUrlResult = apiConfigHelper.buildBffApiUrl(MANAGER_VIEW_APP_PREFIX, locHref, `/lenderList/${dealerId}`);
    const isPaperLenderEnabled = featureToggleSelectors.isPaperLenderEnabled(state);

    let endpoint = bffApiUrlResult.url;
    if (isPaperLenderEnabled) {
        const dealerState = dealerSelectors.getDealerStateCode(state);

        const params = new URLSearchParams();
        if (dealerState) params.set('dealerState', dealerState);
        params.set('financeMethod', getProductForOfferType(getCurrentOfferType(state)));
        params.set('applicantType', getApplicantType(state));
        params.set('collateralType', 'Auto');
        params.set('lenderType', 'All');

        endpoint = `${bffApiUrlResult.url}?${params.toString()}`;
    }

    return {
        [RSAA]: {
            endpoint,
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            },
            types: [FETCH_LENDER_LIST_REQUEST, FETCH_LENDER_LIST_SUCCESS, FETCH_LENDER_LIST_FAILURE]
        }
    };
};

/**
 * Seeds the cache from lender-address data already on the deal (no BFF fetch): the titling address plus the
 * credit-decision lender's address. Lets a re-selection restore both without a network call.
 */
export const cacheLender =
    (lenderId: string, response?: LenderAddressResponse) =>
    (dispatch: Dispatch<AnyAction, StateTree>, getState: () => StateTree) => {
        const state = getState();
        const creditDecisionLender = getCreditDecisionLender(state);
        /**
         * There is no possibility of this condition as we are start caching once we already have the data in the creditDecisionLender
         * But for the type safty we are puttting the check here.
         */
        if (!creditDecisionLender?.id) return;
        const offerType = getCurrentOfferType(state);
        const isLease = offerType === LEASE;
        dispatch({
            type: CACHE_LENDER_ITEM,
            payload: {
                offerType,
                lenderId,
                titlingLender: response ? deriveTitlingLender(response, isLease) : toLenderData(getTitlingLenderWithAddress(state)),
                creditDecisionLender: response
                    ? /* Ideally, creditDecisionLender should not be undefined, but it is required for caching, so we cache only if it contains data.
                      This is verified above with the condition `if (!creditDecisionLender?.id)`
                      So, we typecast creditDecisionLender into ICreditDecisionLender, which is a safe operation */
                      deriveCreditDecisionLenderAddress(response, creditDecisionLender as ICreditDecisionLender)
                    : creditDecisionLender
            }
        });
    };

export const clearLenderAddressError = () => ({ type: CLEAR_LENDER_ADDRESS_ERROR });

/**
 * Stores a manually-corrected titling address in the scoped (per-scenario) cache so it is reused instead of
 * being overwritten by a re-fetch when the user re-selects this lender.
 *
 * Also clears any outstanding fetch error, since a manually entered address resolves the gap the failure
 * alerts report — leaving `hasError` set would strand those alerts after the user has already fixed it.
 */
export const updateTitlingAddressCache =
    (lenderId: string, address?: TitlingLender) => (dispatch: Dispatch<AnyAction, StateTree>, getState: () => StateTree) => {
        dispatch({
            type: CACHE_LENDER_ADDRESS_EDIT,
            payload: { offerType: getCurrentOfferType(getState()), lenderId, address: toLenderData(address) }
        });
        dispatch(clearLenderAddressError());
    };

export const syncLenderAddressRedDotErrors = () => (dispatch: Dispatch<AnyAction, StateTree>, getState: () => StateTree) => {
    const state = getState();
    if (!featureToggleSelectors.enableLenderAddress(state)) return;

    const address = (getTitlingLenderWithAddress(state) ?? {}) as Record<string, string | null | undefined>;
    const engine = createValidationEngine({
        initialConfig: lenderValidationConfig,
        initialValues: LENDER_ADDRESS_FIELD_PATHS.reduce((values, path) => {
            values[path] = address[path] ?? '';
            return values;
        }, {} as Record<string, string>)
    });

    const formErrors = engine.getFormErrorState({ channel: 'inline' });
    const errorFields = LENDER_ADDRESS_FIELD_PATHS.reduce((fields, path) => {
        fields[path] = Boolean(formErrors?.[path]?.hasError);
        return fields;
    }, {} as Record<string, boolean>);

    dispatch(setErrorFields(DealTabs.LENDER, errorFields, true));
};

/**
 * Applies the cached (already-transformed) lender addresses into offer redux. Shared by the fetch-success
 * path and the cache-hit path, so both hydrate the fields identically without an API round trip. The cached
 * titling address is whatever is current for the lender — the server-resolved value or a manual edit.
 *
 * Both paths converge here, so this is also the single place lender-address resolution is logged to New Relic:
 * `cacheHit` distinguishes a cache hit from an API fetch, and the logged `lenderAddress` is uniform (always the
 * cached item) regardless of path. `meta` carries the offer type and the selected (credit-decision) lender id.
 */
export const applyLenderResponse =
    (lenderId: string, cacheHit = false) =>
    (dispatch: Dispatch<AnyAction, StateTree>, getState: () => StateTree) => {
        const state = getState();
        const offerType = getCurrentOfferType(state);
        const cachedLender = lenderAddressSelectors.getCachedLender(state, offerType, lenderId);

        logNewRelicPageAction('MV:UI:ApplyLenderAddressWithCache', {
            lenderAddress: cachedLender,
            meta: { cacheHit, offerType, lenderId }
        });

        if (cachedLender) {
            dispatch(updateTitlingLenderWithAddress(cachedLender.titlingLender));
            dispatch(updateCreditDecisionLenderWithAddress(cachedLender.creditDecisionLender));
            dispatch(syncLenderAddressRedDotErrors());
        }
    };

/**
 * Fetch lender addresses for the selected lender. Resolves from the scoped cache first (server-resolved or
 * user-edited), and only falls through to the BFF `/lenders/address` endpoint on a miss — so re-selecting a
 * lender already resolved this session costs no network call. `forceRefresh` skips the cache.
 */
export const fetchLenderAddress =
    (
        lenderId: string | undefined,
        {
            forceRefresh = false,
            rebaselineUnsavedChangesWhenClean = false
        }: { forceRefresh?: boolean; rebaselineUnsavedChangesWhenClean?: boolean } = {}
    ) =>
    (dispatch: Dispatch<AnyAction, StateTree>, getState: () => StateTree) => {
        if (!lenderId) return;
        const state = getState();

        if (!forceRefresh) {
            const cachedLenderAddress = lenderAddressSelectors.getCachedLender(state, getCurrentOfferType(state), lenderId);
            if (cachedLenderAddress) {
                dispatch(clearLenderAddressError());
                dispatch(applyLenderResponse(lenderId, true));
                return;
            }
        }

        // Clear the previous lender's address immediately so no stale data lingers while the new one loads.
        dispatch(updateTitlingLenderWithAddress(toLenderData()));

        const locHref = locHrefUtil.getLocHref();
        const bffApiUrlResult = apiConfigHelper.buildBffApiUrl(MANAGER_VIEW_APP_PREFIX, locHref, '/lenders/address');
        const request = getLenderAddressRequest(lenderId, state);
        return dispatch({
            [RSAA]: {
                endpoint: bffApiUrlResult.url,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    [DEAL_XG_DEAL_ID]: getDealXgId(state)
                },
                body: JSON.stringify(request),
                types: [
                    { type: FETCH_LENDER_ADDRESS_REQUEST, meta: { lenderId } },
                    {
                        type: FETCH_LENDER_ADDRESS_SUCCESS,
                        meta: { lenderId, ...(rebaselineUnsavedChangesWhenClean && { rebaselineUnsavedChangesWhenClean: true }) }
                    },
                    { type: FETCH_LENDER_ADDRESS_FAILURE, meta: { lenderId } }
                ]
            }
        });
    };

/** Map the deal type to the API `product` param (Finance/Flexbuy => Retail, Lease => Lease, Balloon => Balloon). */
export const getProductForOfferType = (offerType: string): string => {
    switch (offerType) {
        case LEASE:
            return 'Lease';
        case BALLOON:
            return 'Balloon';
        default:
            return 'Retail';
    }
};

const getLenderAddressRequest = (lenderId: string, state: StateTree): LenderAddressApiRequest => {
    const dealerState = dealerSelectors.getDealerStateCode(state) ?? '';
    const product = getProductForOfferType(getCurrentOfferType(state));
    const commonOrgId = `${getCommonOrgId(state) ?? ''}`;

    return { lenderId, dealerState, product, commonOrgId };
};
